import { KeyboardEventHandler } from 'react';
import { FieldValues } from 'react-hook-form';
import { t } from 'utils/getTranslations';
import { isValidNumber } from '../rhf-validators/validators';
import TextInput, { TextInputProps } from './TextInput';

interface Props {
    allowNegative?: boolean;
    onlyNegative?: boolean;
    onKeyDown?: KeyboardEventHandler<HTMLDivElement>;
}

const DecimalInput = <T extends FieldValues>(props: TextInputProps<T> & Props) => {
    // Backend cannot handle comma value. Lets allow user to use comma but just change it to dot. win-win
    const replaceDotWithComma = (str: string) => {
        return str.replace(/,/g, '.');
    };

    const { allowNegative, onlyNegative, onKeyDown, validate, decimalScale = 2, ...restProps } = props;

    const transformEventBeforeChange = (
        e: React.ChangeEvent<HTMLInputElement>
    ): React.ChangeEvent<HTMLInputElement> => {
        if (e?.target?.value) {
            let value = replaceDotWithComma(e.target.value);

            // If onlyNegative is true, handle minus sign logic
            if (onlyNegative) {
                // Allow user to keep a lone "-" while still typing when allowNegative is also set
                if (value === '-' && allowNegative) {
                    // keep as-is
                }
                // If value is only "-" or contains no digits or is zero, remove the minus
                else if (value === '-' || !value || !/\d/.test(value) || parseFloat(value) === 0) {
                    value = value.replace('-', '');
                }
                // If value has digits but no minus and is not zero, prepend it
                else if (!value.startsWith('-') && /\d/.test(value) && parseFloat(value) !== 0) {
                    value = '-' + value;
                }
            }

            e.target.value = value;
        }
        return e;
    };

    return (
        <TextInput
            pattern={`^${allowNegative || onlyNegative ? '-?' : ''}[0-9]*[.,]?([0-9]{0,2})?$`}
            decimalScale={decimalScale}
            {...restProps}
            validate={{
                ...(typeof validate === 'function' ? { custom: validate } : validate),
                mustBeNumber: isValidNumber(t('validation.must-be-number')),
            }}
            transformEventBeforeChange={transformEventBeforeChange}
            onKeyDown={onKeyDown}
        />
    );
};

export default DecimalInput;
